import { catalogSourceKey, normalizeXtreamCatalog, redactCatalog, XtreamError } from "@hybrid-iptv/iptv-parser";
import type { CatalogLists, XtreamClient, XtreamSourceFields } from "@hybrid-iptv/iptv-parser";
import { catalogRowKeys } from "../library/catalogQuery.js";
import { beginCatalogWork, endCatalogWork } from "../library/catalogRebuildStore.js";
import type { CatalogItem, CatalogKind, CatalogPage, CatalogQuery, ShelfFillRequest } from "../library/catalogQuery.js";

/**
 * The APK's Xtream catalog, page side, when the native Catalog plugin is there
 * (native/android/.../catalog/CatalogPlugin.java). Native code downloads the
 * six lists and stores only the fields the app uses; the page reads them back a
 * page at a time and normalises them with the SAME normalizeXtreamCatalog the
 * server and the old path use, so every id and field is identical.
 *
 * Why (owner 2026-09-19): a provider's lists arrived on the page as one string
 * each (44,000+ movies here; 2-3x that on other providers), parsed, normalised
 * and copied again. On the 1 GB projector the load took ~110 MB of the device's
 * free memory and froze the page; a 5 MB download through the same bridge
 * crashed it. This is stage A; stage B has the screens ask for pages instead of
 * holding the whole catalog.
 */

interface CapacitorLike {
  isNativePlatform?: () => boolean;
  PluginHeaders?: Array<{ name: string }>;
  nativePromise?: (plugin: string, method: string, options: Record<string, unknown>) => Promise<unknown>;
}

const PLUGIN = "Catalog";
/** Items per bridge call: one page's JSON stays around a megabyte. */
const PAGE = 5_000;
/**
 * Whether the normalised store a device already holds (`held`) may keep answering while the one for
 * `wanted` is built behind it. Same source and layout (only the download time differs) always may;
 * so may a layout that only ADDS columns native can do without: rows6 added the duplicate flags,
 * and a store without them just shows every stream, as before. rows7 (2026-10-19) decodes HTML
 * character references in names ("&#127875;", "&amp;"); an older store keeps answering with the
 * codes showing until the rebuild lands. A cold start after such an update
 * need not wait minutes for the rebuild (owner, 2026-10-13: "make it as fast as possible").
 */
const COMPATIBLE_LAYOUTS = ["rows5", "rows6", "rows7"];

export function canKeepAnswering(held: string | null, wanted: string): boolean {
  if (held === null) return false;
  const tail = (v: string) => v.slice(v.indexOf(":"));
  if (tail(held) === tail(wanted)) return true;
  const layoutOf = (v: string) => v.slice(v.lastIndexOf(":") + 1);
  const sourceOf = (v: string) => tail(v).slice(0, tail(v).lastIndexOf(":"));
  return (
    COMPATIBLE_LAYOUTS.includes(layoutOf(held)) &&
    COMPATIBLE_LAYOUTS.includes(layoutOf(wanted)) &&
    sourceOf(held) === sourceOf(wanted)
  );
}

/** Fired when a rebuild that ran behind a usable older build has swapped the new one in. */
export const CATALOG_NORMALISED_EVENT = "slique:catalog-normalised";
/** A catalog older than the TTL but younger than this is shown at once while a new one downloads. */
const STALE_OK_MS = 7 * 24 * 60 * 60 * 1000;

type Kind = "live" | "vod" | "series";
type Raw = Record<string, unknown>;

interface SyncResult {
  fetchedAt: number;
  live: number;
  vod: number;
  series: number;
  cached: boolean;
  stale: boolean;
}

function capacitor(): CapacitorLike | null {
  const cap = (globalThis as { Capacitor?: CapacitorLike }).Capacitor;
  if (cap?.isNativePlatform?.() !== true || typeof cap.nativePromise !== "function") return null;
  if (!cap.PluginHeaders?.some((header) => header.name === PLUGIN)) return null;
  return cap;
}

/** True inside an APK that carries the native catalog. */
export function nativeCatalogAvailable(): boolean {
  return capacitor() !== null;
}

const ERROR_CODES = new Set(["timeout", "unreachable", "http_error", "malformed_response", "invalid_credentials"]);

async function call<T>(method: string, options: Record<string, unknown>): Promise<T> {
  const cap = capacitor();
  if (!cap) throw new XtreamError("malformed_response", "native catalog unavailable");
  try {
    return (await cap.nativePromise!(PLUGIN, method, options)) as T;
  } catch (error) {
    const { code, message } = (error ?? {}) as { code?: unknown; message?: unknown };
    if (typeof code === "string" && ERROR_CODES.has(code)) {
      throw new XtreamError(code as XtreamError["code"], typeof message === "string" && message ? message : undefined);
    }
    throw new XtreamError("malformed_response", "catalog failed");
  }
}

/**
 * Bulk does not go through the bridge.
 *
 * Capacitor's message channel lands on Android's MAIN thread and parses the
 * whole message with org.json before any plugin sees it, and with logging on it
 * then serialises the parsed tree back to a string for logcat. A page of this
 * catalog is about a megabyte, so each one was read, built into a tree and
 * written out again on the thread that draws the screen. Measured on the 1 GB
 * projector (2026-09-22): 39.7 s of main-thread stalls inside that path, the
 * worst freeze 22.2 s — well past the 5 s at which Android kills an app.
 *
 * So each direction takes the road native apps take. OUT of native: an ordinary
 * same-origin GET that the APK answers from its own store
 * (MainActivity.catalogPage), which Chromium parses itself, off Android's main
 * thread. INTO native: the string is parked on `window.sliqueBulk` — a plain
 * JavaScript interface, JNI, no parsing, delivered on a background thread — and
 * the bridge carries only its name.
 *
 * Both fall back to the old bridge call, because the web assets and the native
 * shell update separately: a build has to survive meeting the other version of
 * itself, and the website has neither.
 */
const PAGE_ROUTE = "/_native/catalog/page";

interface BulkChannel {
  stage: (caller: string, payload: string) => string;
}

function bulkChannel(): BulkChannel | null {
  const bulk = (globalThis as { sliqueBulk?: BulkChannel }).sliqueBulk;
  return typeof bulk?.stage === "function" ? bulk : null;
}

/**
 * The nonce that lets this page use the bulk channel.
 *
 * A javascript interface is injected into every frame in the WebView, and this
 * app loads one it does not own — the YouTube iframe behind the Trailer button.
 * The channel is therefore useless without a nonce that only comes back over
 * Capacitor's bridge, which answers the main frame alone. Asked for once and
 * kept; a failure here just means the rows go the old way.
 */
let bulkNonce: Promise<string | null> | null = null;

function nonceForBulk(): Promise<string | null> {
  bulkNonce ??= call<{ nonce?: string }>("bulkNonce", {})
    .then((result) => (typeof result.nonce === "string" && result.nonce ? result.nonce : null))
    .catch(() => null);
  return bulkNonce;
}

async function readPage(table: "item" | "category", kind: Kind, offset: number, limit: number): Promise<Raw[]> {
  if (typeof fetch === "function") {
    try {
      const url = `${PAGE_ROUTE}?table=${table}&kind=${kind}&offset=${offset}&limit=${limit}`;
      const response = await fetch(url);
      // Only the APK answers this path. Anywhere else the request falls
      // through to the app shell, which is HTML — so check before trusting it.
      if (response.ok && (response.headers.get("content-type") ?? "").includes("application/json")) {
        return (await response.json()) as Raw[];
      }
    } catch {
      // No route (older shell, or the website): the bridge still works.
    }
  }
  const { json } = await call<{ json: string }>("page", { table, kind, offset, limit });
  return JSON.parse(json) as Raw[];
}

/** Hand native a page of normalised rows: parked where it can, over the bridge otherwise. */
async function writeNormPage(kind: Kind, rows: unknown[][]): Promise<void> {
  const payload = JSON.stringify(rows);
  const bulk = bulkChannel();
  if (bulk) {
    try {
      const caller = await nonceForBulk();
      const token = caller ? bulk.stage(caller, payload) : "";
      if (token) {
        await call("normPut", { kind, token });
        return;
      }
    } catch {
      // Parking failed (an older shell has no channel, or no nonce): fall through.
    }
  }
  await call("normPut", { kind, rows: payload });
}

async function eachPage(kind: Kind, total: number, use: (page: Raw[]) => void): Promise<void> {
  for (let offset = 0; offset < total; offset += PAGE) {
    const page = await readPage("item", kind, offset, PAGE);
    if (page.length === 0) return;
    use(page);
  }
}

async function readAll(table: "item" | "category", kind: Kind, total: number | null): Promise<Raw[]> {
  const out: Raw[] = [];
  for (let offset = 0; ; offset += PAGE) {
    const page = await readPage(table, kind, offset, PAGE);
    for (const item of page) out.push(item);
    if (page.length < PAGE || (total !== null && out.length >= total)) return out;
  }
}

function listUrls(client: XtreamClient): Record<string, string> {
  return {
    live_categories: client.apiUrl("get_live_categories").toString(),
    live: client.apiUrl("get_live_streams").toString(),
    vod_categories: client.apiUrl("get_vod_categories").toString(),
    vod: client.apiUrl("get_vod_streams").toString(),
    series_categories: client.apiUrl("get_series_categories").toString(),
    series: client.apiUrl("get_series").toString(),
  };
}

/**
 * A connection test's counts (countXtreamContent's), from a fresh native
 * download: the lists never cross to the page, and the catalog the app asks for
 * next is already stored.
 */
export async function nativeXtreamCounts(source: XtreamSourceFields, client: XtreamClient) {
  const sync = await call<SyncResult>("sync", { urls: listUrls(client), key: catalogSourceKey(source), force: true });
  return {
    channelCount: sync.live,
    movieCount: sync.vod,
    seriesCount: sync.series,
    contentCount: sync.live + sync.vod + sync.series,
  };
}

/**
 * The catalog for a validated Xtream client, as buildXtreamCatalog returns it:
 * the same category-name map, the same normaliser, the same redaction.
 */
export async function nativeXtreamCatalog(
  source: XtreamSourceFields,
  client: XtreamClient,
  idNamespace: string,
  ttlMs: number,
  /** Stage B: the APK's screens ask native for movies and series, so the
   * channel list is all the page builds; pass true only for the whole catalog. */
  includeVod = false,
): Promise<CatalogLists> {
  const urls = listUrls(client);
  // The identity includes the password, so a changed login never reads the old account's
  // catalog. Native stores only its hash.
  const sync = await call<SyncResult>("sync", {
    urls,
    key: catalogSourceKey(source),
    maxAgeMs: ttlMs,
    staleMs: STALE_OK_MS,
  });
  if (!sync.cached) {
    console.info(`[catalog] downloaded natively: ${sync.live} channels, ${sync.vod} movies, ${sync.series} series`);
  }

  const categories = [
    ...(await readAll("category", "live", null)),
    ...(await readAll("category", "vod", null)),
    ...(await readAll("category", "series", null)),
  ];
  const categoryNames = new Map(
    categories
      .filter((category) => category.category_id !== undefined && category.category_name)
      .map((category) => [String(category.category_id), category.category_name as string]),
  );

  // Normalised a page at a time, so a page's raw rows are dropped before the next arrives.
  const normalise = (kind: Kind, page: Raw[]) => {
    const catalog = normalizeXtreamCatalog(
      (kind === "live" ? page : []) as never,
      (kind === "vod" ? page : []) as never,
      (kind === "series" ? page : []) as never,
      idNamespace,
      client,
      categoryNames,
    );
    return redactCatalog(catalog);
  };
  const result: CatalogLists = { channels: [], vod: [], series: [] };
  let position = 0;
  await eachPage("live", sync.live, (page) => {
    // normalizeXtreamCatalog names an unnamed channel by its position in the WHOLE list.
    for (const item of page) {
      position += 1;
      if (typeof item.name !== "string" || !item.name.trim()) item.name = `Channel ${position}`;
    }
    for (const channel of normalise("live", page).channels) result.channels.push(channel);
  });
  if (!includeVod) return result;
  await eachPage("vod", sync.vod, (page) => {
    for (const movie of normalise("vod", page).vod) result.vod.push(movie);
  });
  await eachPage("series", sync.series, (page) => {
    for (const show of normalise("series", page).series) result.series.push(show);
  });
  return result;
}

// --- Stage B: the normalised catalog, written back once per download --------

let normalising: { version: string; done: Promise<void> } | null = null;

/**
 * Make sure native holds this download's catalog NORMALISED, with the keys the
 * screens query on (catalogRowKeys). Built a page at a time, never held whole;
 * once per download (the version is the download's time and the id namespace).
 */
export async function ensureNativeNormalised(
  source: XtreamSourceFields,
  client: XtreamClient,
  idNamespace: string,
  ttlMs: number,
  /** "Refresh now": download again whatever the stored copy's age. */
  force = false,
): Promise<void> {
  // The notice covers the download as well as the rebuild (owner, 2026-09-20),
  // but only when there IS a download: a stored catalog answers in a few
  // milliseconds, and a notice that flashes on every check would be worse than
  // none. So it appears only once the call has been running a moment.
  // This call's own piece of the notice, and the only one it may ever put
  // down. Another caller's rebuild is not ours to declare finished.
  let downloading = 0;
  const slow = window.setTimeout(() => {
    downloading = beginCatalogWork();
  }, 1500);
  let sync: SyncResult;
  try {
    sync = await call<SyncResult>("sync", {
      urls: listUrls(client),
      key: catalogSourceKey(source),
      maxAgeMs: ttlMs,
      staleMs: STALE_OK_MS,
      force,
    });
  } catch (error) {
    window.clearTimeout(slow);
    endCatalogWork(downloading);
    throw error;
  }
  window.clearTimeout(slow);
  endCatalogWork(downloading);
  // The trailing tag names the row layout: a new one re-normalises stores built before it.
  const version = `${sync.fetchedAt}:${idNamespace}:rows7`;
  const state = await call<{ version?: string | null }>("normState", {});
  // A slow download that needed no rebuild. Its own share of the notice is
  // already down (above); anything still showing belongs to another caller.
  if (state.version === version) return;
  // A finished build for the same source and row layout (only the download
  // time differs) keeps answering while the new one is built behind it: native
  // swaps the new one in only when it is complete.
  const held = typeof state.version === "string" ? state.version : null; // native omits it when there is none
  const usable = canKeepAnswering(held, version);
  if (normalising?.version === version) return usable ? undefined : normalising.done;
  const rebuilding = beginCatalogWork();
  const done = (async () => {
    const categories = [
      ...(await readAll("category", "vod", null)),
      ...(await readAll("category", "series", null)),
    ];
    const categoryNames = new Map(
      categories
        .filter((category) => category.category_id !== undefined && category.category_name)
        .map((category) => [String(category.category_id), category.category_name as string]),
    );
    await call("normReset", { version });
    // Where the time goes (logged below): reading the stored pages, working the items out, handing
    // the rows to native. A cold start after an update waits for this, so it is measured.
    const spent = { read: 0, work: 0, write: 0 };
    const startedAt = Date.now();

    for (const kind of ["vod", "series"] as const) {
      let pos = 0;
      await eachPageAsync(kind, kind === "vod" ? sync.vod : sync.series, spent, async (page) => {
        const workStarted = Date.now();
        const catalog = redactCatalog(normalizeXtreamCatalog(
          [],
          (kind === "vod" ? page : []) as never,
          (kind === "series" ? page : []) as never,
          idNamespace,
          client,
          categoryNames,
        ));
        const items: CatalogItem[] = kind === "vod" ? catalog.vod : catalog.series;
        const rows = items.map((item) => {
          const keys = catalogRowKeys(item);
          return [
            item.id,
            pos++,
            item.categoryId ?? null,
            item.rawTitle,
            keys.titleLc,
            keys.searchKey,
            keys.titleKey,
            "year" in item && item.year !== undefined ? item.year : null,
            item.tmdbId ?? null,
            JSON.stringify(item),
            keys.shelfOk ? 1 : 0,
            keys.rating,
            keys.dupKey,
            keys.quality,
          ];
        });
        const writeStarted = Date.now();
        spent.work += writeStarted - workStarted;
        await writeNormPage(kind, rows);
        spent.write += Date.now() - writeStarted;
      });
    }
    const finishStarted = Date.now();
    await call("normFinish", { version });
    const timings = { total: Date.now() - startedAt, read: spent.read, work: spent.work, write: spent.write, finish: Date.now() - finishStarted };
    // Kept where the remote tools can read it: the device log does not carry page console output.
    (globalThis as { __catalogNormTimings?: unknown }).__catalogNormTimings = timings;
    console.info(`[catalog] normalised natively: ${sync.vod} movies, ${sync.series} series`, timings);
  })();
  normalising = { version, done };
  const settled = done.finally(() => {
    if (normalising?.done === done) normalising = null;
    endCatalogWork(rebuilding);
  });
  if (usable) {
    settled
      // The lists on screen were read from the older build: ask for them again from the new one.
      .then(() => {
        if (typeof window !== "undefined") window.dispatchEvent(new Event(CATALOG_NORMALISED_EVENT));
      })
      .catch((error: unknown) => console.warn("[catalog] background normalisation failed", error));
    return;
  }
  await settled;
}

async function eachPageAsync(
  kind: Kind,
  total: number,
  spent: { read: number },
  use: (page: Raw[]) => Promise<void>,
): Promise<void> {
  for (let offset = 0; offset < total; offset += PAGE) {
    const readStarted = Date.now();
    const page = await readPage("item", kind, offset, PAGE);
    spent.read += Date.now() - readStarted;
    if (page.length === 0) return;
    await use(page);
  }
}

/** A catalog query answered by native SQLite. */
export async function nativeCatalogQuery(query: CatalogQuery, excludeCategories: string[]): Promise<CatalogPage> {
  const result = await call<{ total: number; json: string }>("query", {
    kind: query.kind,
    excludeCategories,
    category: query.category ?? null,
    ids: query.ids ?? null,
    tmdbIds: query.tmdbIds ?? null,
    titleKeys: query.titleKeys ?? null,
    dupKey: query.dupKey ?? null,
    text: query.text ? query.text.toLowerCase() : null,
    search: query.search ?? null,
    sort: query.sort ?? "pos",
    offset: query.offset ?? 0,
    limit: query.limit,
  });
  return { total: result.total, items: JSON.parse(result.json) as CatalogItem[] };
}

/** Home's genre-row top-up (shelfFill.ts pickShelfFill), answered natively. */
export async function nativeShelfFill(request: ShelfFillRequest, excludeCategories: string[]): Promise<CatalogItem[]> {
  const { json } = await call<{ json: string }>("shelfFill", { ...request, excludeCategories });
  return JSON.parse(json) as CatalogItem[];
}

/** Distinct categories and their counts, from native. */
export async function nativeCatalogCategories(kind: CatalogKind): Promise<Array<[string, number]>> {
  const { json } = await call<{ json: string }>("categories", { kind });
  return JSON.parse(json) as Array<[string, number]>;
}

/** One title of a discovery row: the catalog item, with the discovery file's TMDB id, name and year. */
export interface DiscoveryEntry {
  tmdb: number;
  label?: string;
  year?: number;
  item: CatalogItem;
}

/** Home's rows for one side on one day (CatalogStore.discoveryRows). */
export interface DiscoveryRowsResult {
  trendingYear: number | null;
  /** trending, popular and the genre rows (SHELF_IDS), in Home order. */
  rows: Record<string, DiscoveryEntry[]>;
  spotlight: { type: string; title: string; items: DiscoveryEntry[] } | null;
}

export interface DiscoveryRowsRequest {
  kind: CatalogKind;
  /** localDayIndex: a reload the same day gives the same rows. */
  day: number;
  /** Tiles per row (Settings). */
  size: number;
  kids: boolean;
}

/**
 * Fetches the discovery files when due (Section 26 step 4; DiscoveryFiles.java) and swaps in
 * a new discovery.db when one changed. `ready` is whether the device has rows to give.
 */
export async function nativeDiscoveryRefresh(force = false): Promise<{ changed: boolean; ready: boolean }> {
  return call<{ changed: boolean; ready: boolean }>("discoveryRefresh", { force });
}

/** The day's rows from discovery.db over this catalog, or null when the device has no discovery files yet. */
export async function nativeDiscoveryRows(
  request: DiscoveryRowsRequest,
  excludeCategories: string[],
  kidsCategories: string[],
): Promise<DiscoveryRowsResult | null> {
  const { json } = await call<{ json?: string | null }>("discoveryRows", { ...request, excludeCategories, kidsCategories });
  return json ? (JSON.parse(json) as DiscoveryRowsResult) : null;
}

/** A title page's Similar and You May Also Like (CatalogStore.discoveryRelated). */
export interface DiscoveryRelatedResult {
  similar: DiscoveryEntry[];
  youMayLike: DiscoveryEntry[];
}

export interface DiscoveryRelatedRequest {
  kind: CatalogKind;
  /** The open title's catalog item id. */
  itemId: string;
  size: number;
  kids: boolean;
  /** The profile's history and My List, as catalog item ids: its taste, and what is left out. */
  watchedIds: string[];
  favouriteIds: string[];
}

/** The open title's related rows, or null when the device has no discovery files or does not know the title. */
export async function nativeDiscoveryRelated(
  request: DiscoveryRelatedRequest,
  excludeCategories: string[],
  kidsCategories: string[],
): Promise<DiscoveryRelatedResult | null> {
  const { json } = await call<{ json?: string | null }>("discoveryRelated", { ...request, excludeCategories, kidsCategories });
  return json ? (JSON.parse(json) as DiscoveryRelatedResult) : null;
}

export interface DiscoveryRecommendedRequest {
  kind: CatalogKind;
  size: number;
  kids: boolean;
  /** How many titles the profile has in all, films and series together (shapes the taste share). */
  tasteTotal: number;
  /** localDayIndex: the row rotates daily. */
  day: number;
  /** The profile's history and My List, as catalog item ids. */
  watchedIds: string[];
  favouriteIds: string[];
}

/** Home's "Recommended For You" for one side, in row order (CatalogStore.discoveryRecommended); null without discovery.db. */
export async function nativeDiscoveryRecommended(
  request: DiscoveryRecommendedRequest,
  excludeCategories: string[],
  kidsCategories: string[],
): Promise<DiscoveryEntry[] | null> {
  const { json } = await call<{ json?: string | null }>("discoveryRecommended", { ...request, excludeCategories, kidsCategories });
  return json ? (JSON.parse(json) as DiscoveryEntry[]) : null;
}

/** The open title's cast and directors or creators, as Wikidata numbers (CatalogStore.discoveryPeople). */
export interface DiscoveryPeople {
  cast: number[];
  people: number[];
}

/** The open title's people, or null when the device has no discovery files or does not know the title. */
export async function nativeDiscoveryPeople(kind: CatalogKind, itemId: string): Promise<DiscoveryPeople | null> {
  const { json } = await call<{ json?: string | null }>("discoveryPeople", { kind, itemId });
  return json ? (JSON.parse(json) as DiscoveryPeople) : null;
}
